import { call, put, takeEvery, takeLatest } from "redux-saga/effects";

// Login Redux States
import { LOGIN_USER, LOGOUT_USER, SOCIAL_LOGIN, LOGIN_USER_DATA, LOGIN_DATA_RESET, LOGOUT_USER_CLEAR } from "./actionTypes";
import { apiError, loginSuccess, logoutUserSuccess, loginError, loginDataReset, loginSuccessData, logoutUserSuccessClear } from "./actions";

//Include Both Helper File with needed methods
import { getFirebaseBackend } from "../../../helpers/firebase_helper";
import {
  postFakeLogin,
  postJwtLogin,
  postSocialLogin,
  postLogin
} from "../../../helpers/fakebackend_helper";
import { postLogin as postBackendLogin, postLogout as postBackendLogout } from "../../../helpers/backend_helper";
import { mockLogin, mockLogout, isMockAuthEnabled } from "../../../helpers/mock_auth_helper";

const fireBaseBackend = getFirebaseBackend();

function* loginUser({ payload: { user, history } }) {
  try {
    if (process.env.REACT_APP_DEFAULTAUTH === "firebase") {
      const response = yield call(
        fireBaseBackend.loginUser,
        user.email,
        user.password
      );
      yield put(loginSuccess(response));
    } else if (process.env.REACT_APP_DEFAULTAUTH === "jwt") {
      const response = yield call(postJwtLogin, {
        email: user.email,
        password: user.password,
      });
      localStorage.setItem("authUser", JSON.stringify(response));
      yield put(loginSuccess(response));
    } else if (process.env.REACT_APP_DEFAULTAUTH === "fake") {
      const response = yield call(postFakeLogin, {
        email: user.email,
        password: user.password,
      });

      localStorage.setItem("authUser", JSON.stringify(response));
      yield put(loginSuccess(response));
    }

    history('/dashboard');
  } catch (error) {
    yield put(apiError(error));
  }
}

function* logoutUser({ payload: { history } }) {
  try {
    // Call backend logout endpoint
    // Note: We use a try-catch here because logout might fail if token is expired
    // but we still want to clear local storage and redirect
    try {
      yield call(postBackendLogout);
    } catch (logoutError) {
      console.warn("Backend logout failed (token may be expired):", logoutError);
      // Continue with local cleanup even if backend logout fails
    }

    // Clear local storage regardless of backend logout result
    localStorage.removeItem("authUser");
    localStorage.removeItem("token");
    localStorage.removeItem("branchName");

    if (process.env.REACT_APP_DEFAULTAUTH === "firebase") {
      const response = yield call(fireBaseBackend.logout);
      yield put(logoutUserSuccess(response));
    }
    history('/login');
  } catch (error) {
    // Even if everything fails, clear storage and redirect to login
    localStorage.removeItem("authUser");
    localStorage.removeItem("token");
    localStorage.removeItem("branchName");
    history('/login');
    yield put(apiError(error));
  }
}

function* socialLogin({ payload: { type, history } }) {
  try {
    if (process.env.REACT_APP_DEFAULTAUTH === "firebase") {
      const fireBaseBackend = getFirebaseBackend();
      const response = yield call(fireBaseBackend.socialLoginUser, type);
      if (response) {
        history("/dashboard");
      } else {
        history("/login");
      }
      localStorage.setItem("authUser", JSON.stringify(response));
      yield put(loginSuccess(response));
    }
    if (response)
      history("/dashboard");
  } catch (error) {
    console.log("error", error);
    yield put(apiError(error));
  }
}

function* loginUserData({ payload: { user, history } }) {
  let userData = {
    username: user.userName,
    password: user.password,
  };

  try {
    console.log("[Login] Attempting login with username:", userData.username);
    
    let response;
    
    // Check if mock authentication should be used
    if (isMockAuthEnabled()) {
      console.log("[Login] Using mock authentication (no backend)");
      response = yield call(mockLogin, userData);
    } else {
      console.log("[Login] Using real backend authentication");
      response = yield call(postBackendLogin, userData);
    }
    
    console.log("[Login] Response received:", response);

    // response is now the data directly (not axios response object)
    // Check if response contains the expected user data
    if (response && response.accessToken) {
      localStorage.setItem(
        "authUser",
        JSON.stringify(response)
      );
      localStorage.setItem("token", response.accessToken);
      if (response.user && response.user.branchName) {
        localStorage.setItem("branchName", response.user.branchName);
      }
      yield put(loginSuccessData(response));
      yield put(loginError(""));
      history("/dashboard");
    } else {
      console.error("[Login] Invalid response - missing accessToken:", response);
      yield put(loginError("Login failed. Invalid response from server."));
    }
  } catch (error) {
    console.error("[Login] Error:", error);
    yield put(loginError(error.message || "Login failed. Please check your credentials."));
  }
}

function* onLoginDataReset() {
  put(loginDataReset);
}

function* logoutUserClear({ payload: { history } }) {
  try {
    // Call backend logout endpoint or mock logout
    // Note: We use a try-catch here because logout might fail if token is expired
    // but we still want to clear local storage and redirect
    try {
      if (isMockAuthEnabled()) {
        console.log("[Logout] Using mock logout");
        yield call(mockLogout);
      } else {
        yield call(postBackendLogout);
      }
    } catch (logoutError) {
      console.warn("Logout failed (token may be expired):", logoutError);
      // Continue with local cleanup even if logout fails
    }

    // Clear local storage regardless of backend logout result
    localStorage.removeItem("authUser");
    localStorage.removeItem("token");
    localStorage.removeItem("branchName");

    if (process.env.REACT_APP_DEFAULTAUTH === "firebase") {
      const response = yield call(fireBaseBackend.logout);
      yield put(logoutUserSuccessClear(response));
    }
    history('/login');
  } catch (error) {
    // Even if everything fails, clear storage and redirect to login
    localStorage.removeItem("authUser");
    localStorage.removeItem("token");
    localStorage.removeItem("branchName");
    history('/login');
    yield put(apiError(error));
  }
}

function* authSaga() {
  yield takeEvery(LOGIN_USER, loginUser);
  yield takeLatest(SOCIAL_LOGIN, socialLogin);
  yield takeEvery(LOGOUT_USER, logoutUser);
  yield takeEvery(LOGIN_USER_DATA, loginUserData);
  yield takeEvery(LOGIN_DATA_RESET, onLoginDataReset);
  yield takeEvery(LOGOUT_USER_CLEAR, logoutUserClear);
}

export default authSaga;
